import React, { PropsWithChildren, useContext, useEffect, useState } from "react";
import { AccessControl, Entity, Operations } from "../shared/interfaces/AccessControl";
import { Customer } from "../shared/interfaces/Customer";
import { JwtManager } from "../shared/JwtManager";
import { ApiContext } from "./ApiContext";
import { AuthContext } from "./AuthContext";

interface AccessControlContextType {
  getCustomerListByEntity: (entity: Entity) => Customer[] | undefined;
  canI: (operation: keyof Operations, entity: Entity, customerId?: number) => boolean | undefined;
}

export const AccessControlContext = React.createContext<AccessControlContextType>({} as AccessControlContextType);

const tokenManager = new JwtManager({});

const hasAccess = (accessControlItem: AccessControl, entity: Entity, operation: keyof Operations) => {
  const accessForEntity = accessControlItem[entity];
  return accessForEntity != null && accessForEntity[operation];
};

const useAccessControl = () => {
  const apiContext = useContext(ApiContext);
  const authContext = useContext(AuthContext);
  const [accessControlList, setAccessControlList] = useState<AccessControl[] | undefined>();

  useEffect(() => {
    if (!authContext.userIsAuthenticated) {
      setAccessControlList(undefined);
      return;
    }

    apiContext.accessControlApis.getUserPermissions().then((response: AccessControl[]) => {
      setAccessControlList(response);
    });
  }, [apiContext.accessControlApis, authContext.userIsAuthenticated]);

  return accessControlList;
};

export const AccessControlContextProvider = (props: PropsWithChildren<unknown>) => {
  const accessControlList = useAccessControl();

  function getCustomerListByEntity(entity: Entity): Customer[] | undefined {
    const customers = accessControlList
      ?.filter((accessControlItem: AccessControl) => accessControlItem[entity].view)
      .map((accessControlItem: AccessControl) => {
        return {
          id: accessControlItem.customerId,
          name: accessControlItem.customerName,
        } as Customer;
      });

    return customers && [...new Map(customers.map((v) => [v.id, v])).values()];
  }

  function canI(operation: keyof Operations, entity: Entity, customerId?: number): boolean | undefined {
    const tokenPayload = tokenManager.getPayload();

    if (tokenPayload && tokenPayload.IsAdmin === "IsAdmin") return true;

    if (tokenPayload && tokenPayload.ManagedSurveysSuperAdmin === "ManagedSurveysSuperAdmin") return true;

    if (tokenPayload && entity === "superAdmin" && operation === "add") {
      return tokenPayload.ManagedSurveysSuperAdmin === "ManagedSurveysSuperAdmin";
    }

    if (entity === "resultComments" && tokenPayload && tokenPayload.SubmitUTP2Data === "SubmitUTP2Data") return true;

    if (entity === "panels" || entity === "switchgears") entity = "hvSubstations";

    if (customerId) {
      return accessControlList?.some(
        (accessControlItem: AccessControl) =>
          hasAccess(accessControlItem, entity, operation) && accessControlItem.customerId === customerId,
      );
    } else {
      return accessControlList?.some((accessControlItem: AccessControl) =>
        hasAccess(accessControlItem, entity, operation),
      );
    }
  }

  return (
    <AccessControlContext.Provider
      value={{
        getCustomerListByEntity: getCustomerListByEntity,
        canI: canI,
      }}
    >
      {props.children}
    </AccessControlContext.Provider>
  );
};
